import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './App.css';
import { supabase } from './supabase';

const courseInfo = {
  reiki: {
    name: 'Reiki Certification',
    price: '$99',
    numericPrice: 99,
  },
  coaching: {
    name: 'Intuitive Coaching Certification',
    price: '$149',
    numericPrice: 149,
  },
};

// Helper to check if mobile view
const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 480px)').matches;

function Checkout() {
  const { course } = useParams();
  const navigate = useNavigate();
  const info = courseInfo[course] || { name: 'Course', price: '', numericPrice: 0 };
  const [form, setForm] = useState({ name: '', email: '', mobile: '' });
  const [submitting, setSubmitting] = useState(false);
  const paypalRef = useRef(null);
  const paypalButtonsRef = useRef(null);

  useEffect(() => {
    // Clear any existing PayPal buttons
    if (paypalRef.current) {
      paypalRef.current.innerHTML = '';
    }
    
    // Clean up previous buttons instance
    if (paypalButtonsRef.current) {
      paypalButtonsRef.current = null;
    }
    
    const loadPayPal = () => {
      if (window.paypal && paypalRef.current && !paypalButtonsRef.current) {
        paypalButtonsRef.current = window.paypal.Buttons({
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [{
                amount: {
                  value: info.numericPrice.toString(),
                  currency_code: 'USD'
                },
                description: info.name
              }]
            });
          },
          onApprove: async (data, actions) => {
            try {
              const order = await actions.order.capture();
              
              // Get current form values
              const currentForm = { name: form.name, email: form.email, mobile: form.mobile };
              
              // Save enrollment with PayPal payment
              const enrollmentData = {
                name: currentForm.name || 'PayPal Customer',
                email: currentForm.email || order.payer.email_address,
                mobile: currentForm.mobile || '',
                course_name: info.name,
                course_id: course,
                price: info.price,
                payment_method: 'PayPal',
                payment_id: order.id,
                status: 'paid'
              };
              
              const { error } = await supabase
                .from('course_enrollments')
                .insert([enrollmentData]);
              
              if (error) {
                console.error('Error saving enrollment:', error);
              }
              
              navigate('/thankyou');
            } catch (error) {
              console.error('PayPal payment error:', error);
              alert('Payment processing failed. Please try again.');
            }
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            alert('An error occurred with PayPal. Please try again.');
          }
        });
        
        paypalButtonsRef.current.render(paypalRef.current);
      }
    };

    // Check if PayPal SDK is already loaded
    if (window.paypal) {
      loadPayPal();
    } else {
      // Wait for PayPal SDK to load
      const checkPayPal = setInterval(() => {
        if (window.paypal) {
          clearInterval(checkPayPal);
          loadPayPal();
        }
      }, 100);
      
      return () => {
        clearInterval(checkPayPal);
        if (paypalRef.current) {
          paypalRef.current.innerHTML = '';
        }
        paypalButtonsRef.current = null;
      };
    }
    
    return () => {
      if (paypalRef.current) {
        paypalRef.current.innerHTML = '';
      }
      paypalButtonsRef.current = null;
    };
  }, [info.numericPrice, info.name, info.price, course, navigate]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const enrollmentData = {
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        course_name: info.name,
        course_id: course,
        price: info.price,
        status: 'pending'
      };
      
      const { error } = await supabase
        .from('course_enrollments')
        .insert([enrollmentData]);
      
      if (error) throw error;
      
      setTimeout(() => {
        setSubmitting(false);
        navigate('/thankyou');
      }, 1200);
    } catch (error) {
      console.error('Error saving enrollment:', error);
      // Still navigate even if Supabase fails
      setTimeout(() => {
        setSubmitting(false);
        navigate('/thankyou');
      }, 1200);
    }
  };

  return (
    <div className="main-bg">
      {/* Hide hero-section on mobile */}
      {!isMobile && (
        <header className="hero-section">
          <h1 className="site-title">Checkout</h1>
          <p className="site-tagline">Complete your enrollment for <b>{info.name}</b></p>
        </header>
      )}
      <div className="checkout-section">
        <div className="checkout-card">
          <h2>{info.name}</h2>
          <div className="checkout-price">{info.price}</div>
          <form className="checkout-form" onSubmit={handleSubmit}>
            <input name="name" type="text" placeholder="Your Name" value={form.name} onChange={handleChange} required />
            <input name="email" type="email" placeholder="Your Email" value={form.email} onChange={handleChange} required />
            <input name="mobile" type="tel" placeholder="Mobile Number" value={form.mobile} onChange={handleChange} required />
            <button type="submit" disabled={submitting}>{submitting ? 'Processing...' : 'Enroll & Pay'}</button>
          </form>
          
          <div className="payment-divider">
            <span>OR</span>
          </div>
          
          <div className="paypal-container">
            <h3>Pay with PayPal</h3>
            <div ref={paypalRef} id="paypal-button-container"></div>
          </div>
        </div>
      </div>
      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Life Force Dynamics. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Checkout; 